import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ncbService } from '../services/ncbService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMaximize2, FiX, FiChevronLeft, FiChevronRight, FiPlay, FiVolume2, FiVolumeX, FiInfo } = FiIcons;

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const videoRefs = useRef({});

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const [photoData, videoData] = await Promise.all([
          ncbService.getGalleryImages(),
          ncbService.getGalleryVideos()
        ]);
        
        setPhotos(photoData || []);
        
        // Sort Video Reels by display_order ascending
        const sortedVideos = (videoData || []).sort((a, b) => {
          const orderA = (a.display_order === null || a.display_order === undefined || a.display_order === '') ? 999999 : Number(a.display_order);
          const orderB = (b.display_order === null || b.display_order === undefined || b.display_order === '') ? 999999 : Number(b.display_order);
          
          if (orderA !== orderB) return orderA - orderB;
          return b.id - a.id; // Fallback to newest first
        });
        
        setVideos(sortedVideos);
      } catch (error) {
        console.error("Failed to load gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    loadGallery();
  }, []);

  const handleVideoPlay = (id) => {
    Object.values(videoRefs.current).forEach(v => {
      if (v && v.id !== `video-${id}`) v.pause();
    });
  };

  return (
    <div className="min-h-screen bg-brand-cream pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        <header className="text-center mb-20">
          <h1 className="font-serif text-5xl md:text-7xl text-brand-olive uppercase tracking-[0.1em] mb-6">Visual Gallery</h1>
          <p className="text-sm md:text-base text-brand-olive/60 max-w-2xl mx-auto font-medium">
            Explore the beauty of R. Venue Apartelle through our curated collection of moments and spaces.
          </p>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="space-y-24">
            {/* Photography Section */}
            {photos.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-[1px] flex-1 bg-brand-olive/10"></div>
                  <h2 className="font-serif text-3xl text-brand-olive uppercase tracking-widest">Spaces & Details</h2>
                  <div className="h-[1px] flex-1 bg-brand-olive/10"></div>
                </div>
                <p className="text-center text-sm md:text-base text-brand-olive/60 max-w-3xl mx-auto font-medium mb-10">
                  A quiet look into the spaces, details, and surroundings that make R. Venue Apartelle feel like a relaxed home away from home. Browse through our rooms, shared spaces, and little corners prepared for your stay.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {photos.map((item) => (
                    <motion.div
                      key={`image-${item.id}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      className="relative group overflow-hidden bg-white shadow-sm cursor-pointer aspect-square"
                      onClick={() => setSelectedItem({ ...item, type: 'image' })}
                    >
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-brand-olive/60 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-8">
                        <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                          <p className="text-[10px] font-black uppercase tracking-widest text-brand-cream/60 mb-1">{item.category || 'Photography'}</p>
                          <h3 className="font-serif text-xl text-white uppercase tracking-wider">{item.title || 'Apartelle View'}</h3>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Video Reels Section */}
            {videos.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-[1px] flex-1 bg-brand-olive/10"></div>
                  <h2 className="font-serif text-3xl text-brand-olive uppercase tracking-widest">Moments in Motion</h2>
                  <div className="h-[1px] flex-1 bg-brand-olive/10"></div>
                </div>
                <p className="text-center text-sm md:text-base text-brand-olive/60 max-w-3xl mx-auto font-medium mb-10">
                  See R. Venue Apartelle in motion through short clips that capture the atmosphere, flow, and real feel of the place. These reels give guests a closer glimpse before they arrive.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {videos.map((item) => (
                    <motion.div
                      key={`video-${item.id}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      className="relative group overflow-hidden bg-white shadow-sm cursor-pointer aspect-[9/16]"
                      onClick={() => setSelectedItem({ ...item, type: 'video' })}
                    >
                      <div className="w-full h-full relative">
                        <video
                          ref={el => videoRefs.current[item.id] = el}
                          id={`video-${item.id}`}
                          src={item.video_url}
                          poster={item.thumbnail_url}
                          className="w-full h-full object-cover"
                          muted={item.audio_mode !== 'audio'}
                          onPlay={() => handleVideoPlay(item.id)}
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          loop
                          playsInline
                        />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-100 group-hover:opacity-0 transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/40">
                            <SafeIcon icon={FiPlay} className="text-white text-xl translate-x-0.5" />
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-brand-olive/60 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-8">
                        <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                          <p className="text-[10px] font-black uppercase tracking-widest text-brand-cream/60 mb-1">{item.category || 'Video Reel'}</p>
                          <h3 className="font-serif text-xl text-white uppercase tracking-wider">{item.title || 'Reel Preview'}</h3>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Lightbox */}
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-brand-olive/95 backdrop-blur-xl flex items-center justify-center p-6 md:p-12"
              onClick={() => setSelectedItem(null)}
            >
              <button 
                className="absolute top-8 right-8 text-white/40 hover:text-white transition-colors z-[110]"
                onClick={() => setSelectedItem(null)}
              >
                <SafeIcon icon={FiX} className="text-4xl" />
              </button>

              <div 
                className="relative w-full max-w-6xl flex items-center justify-center"
                onClick={e => e.stopPropagation()}
              >
                {selectedItem.type === 'image' ? (
                  <img 
                    src={selectedItem.image_url} 
                    alt={selectedItem.title}
                    className="max-w-full max-h-[85vh] object-contain shadow-2xl"
                  />
                ) : (
                  <div className="w-full max-w-[400px] aspect-[9/16] relative bg-black shadow-2xl">
                    <video
                      src={selectedItem.video_url}
                      className="w-full h-full object-contain"
                      autoPlay
                      muted={selectedItem.audio_mode !== 'audio'}
                      controls
                      loop
                    />
                  </div>
                )}
                
                <div className="absolute bottom-[-80px] left-0 right-0 text-center">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-2">{selectedItem.category}</p>
                  <h3 className="font-serif text-3xl text-white uppercase tracking-widest">{selectedItem.title}</h3>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Gallery;